import React, { createContext, ReactNode, useContext } from 'react';
import { useParams } from 'react-router-dom';

import { useBulletinQuery, useBulletinsTypes } from '../services/api';
import { getBulletinLastValidationDate } from '../components/BulletinView/Bulletin.helper';
import { BulletinResponse } from '../components/BulletinView/types';

interface BulletinContextType {
  bulletinType?: string;
  bulletinDate?: string;
  bulletinDefaultDate?: string;
  isLatestBulletin: boolean;
  bulletinResponse?: BulletinResponse;
  isLoading: boolean;
  isFetching: boolean;
  error?: Response | null;
}

const BulletinContext = createContext<BulletinContextType>({
  isLatestBulletin: false,
  isLoading: false,
  isFetching: false,
});

function BulletinProvider({ children }: { children?: ReactNode | undefined }) {
  const { bulletinType, bulletinDate: date } = useParams();
  const {
    data: bulletinTypeObject,
    isLoading: isloadingBulletinType,
    isFetching: isFetchingBulletinTypes,
  } = useBulletinsTypes();
  const bulletinDefaultDate = getBulletinLastValidationDate(
    bulletinTypeObject,
    bulletinType,
  );

  const bulletinDate = date ?? bulletinDefaultDate;
  const isLatestBulletin = date === bulletinDefaultDate;

  const { isLoading, isFetching, data, error } = useBulletinQuery(
    bulletinType,
    bulletinDate,
  );

  return (
    <BulletinContext.Provider
      value={{
        bulletinType,
        bulletinDate,
        bulletinDefaultDate,
        isLatestBulletin,
        bulletinResponse: data,
        isLoading: isLoading || isloadingBulletinType,
        isFetching: isFetching || isFetchingBulletinTypes,
        error,
      }}
    >
      {children}
    </BulletinContext.Provider>
  );
}

export function useBulletin() {
  return useContext(BulletinContext);
}

export function useBulletinResponse() {
  const { bulletinResponse } = useContext(BulletinContext);
  if (!bulletinResponse) {
    throw new Error('No bulletin response available');
  }
  return bulletinResponse;
}

export default BulletinProvider;
